// Gemini API Service for sequence building and template generation
import { Node, Edge, MarkerType } from 'reactflow';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || 'AIzaSyCjJqcsXjZ9B3e1zqUunMh3q5DR6c1gk-E';

// Model endpoints in order of preference
const GEMINI_MODELS = [
  {
    name: 'Gemini 2.0 Flash',
    endpoint: `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${GEMINI_API_KEY}`,
    limits: { rpm: 15, tpm: 1000000, rpd: 1500 }
  },
  {
    name: 'Gemini 2.0 Flash-Lite',
    endpoint: `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-lite:generateContent?key=${GEMINI_API_KEY}`,
    limits: { rpm: 30, tpm: 1000000, rpd: 1500 }
  }
];

export interface SequenceRequest {
  description: string;
  objective: string;
  targetAudience: string;
  industry: string;
  channels: string[];
  timeline: string;
}

export interface TemplateRequest {
  type: 'email' | 'sms' | 'linkedin' | 'call';
  category: string;
  description: string;
  personalizationLevel: 'basic' | 'advanced' | 'ai-powered';
  targetAudience: string;
  objective: string;
}

export interface GeneratedSequence {
  name: string;
  description: string;
  nodes: Node[];
  edges: Edge[];
  estimatedDuration: string;
  expectedResults: {
    estimatedReach: number;
    expectedOpenRate: number;
    expectedReplyRate: number;
    estimatedMeetings: number;
  };
}

export interface GeneratedTemplate {
  name: string;
  category: string;
  subject?: string;
  content: string;
  tags: string[];
  variables: string[];
  tips: string[];
}

class GeminiService {
  private static rateLimitTracking = new Map<string, { count: number; resetTime: number }>();
  
  private static async callGeminiAPI(prompt: string, modelIndex = 0): Promise<any> {
    if (modelIndex >= GEMINI_MODELS.length) {
      throw new Error('All Gemini models exhausted');
    }

    const model = GEMINI_MODELS[modelIndex];
    const modelKey = model.name;
    
    // Check rate limits
    const now = Date.now();
    const rateLimit = this.rateLimitTracking.get(modelKey);
    
    if (rateLimit && now < rateLimit.resetTime && rateLimit.count >= model.limits.rpm) {
      // Try next model
      return this.callGeminiAPI(prompt, modelIndex + 1);
    }

    try {
      const response = await fetch(model.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 8192,
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE"
            }
          ]
        })
      });

      if (!response.ok) {
        if (response.status === 429) {
          // Rate limited, try next model
          return this.callGeminiAPI(prompt, modelIndex + 1);
        }
        throw new Error(`Gemini API error: ${response.status}`);
      }

      const data = await response.json();
      
      // Update rate limit tracking
      const currentLimit = this.rateLimitTracking.get(modelKey) || { count: 0, resetTime: now + 60000 };
      if (now >= currentLimit.resetTime) {
        currentLimit.count = 1;
        currentLimit.resetTime = now + 60000;
      } else {
        currentLimit.count++;
      }
      this.rateLimitTracking.set(modelKey, currentLimit);

      return data;
    } catch (error) {
      console.error(`Error with ${model.name}:`, error);
      if (modelIndex < GEMINI_MODELS.length - 1) {
        return this.callGeminiAPI(prompt, modelIndex + 1);
      }
      throw error;
    }
  }

  static async generateSequence(request: SequenceRequest): Promise<GeneratedSequence> {
    const prompt = `
You are an expert email marketing automation specialist. Generate a comprehensive multi-channel sequence based on the following requirements:

Description: ${request.description}
Objective: ${request.objective}
Target Audience: ${request.targetAudience}
Industry: ${request.industry}
Available Channels: ${request.channels.join(', ')}
Timeline: ${request.timeline}

Generate a JSON response with the following structure:
{
  "name": "Sequence Name",
  "description": "Brief description of the sequence",
  "steps": [
    {
      "id": "unique-id",
      "type": "email|sms|linkedin|call|wait|condition|trigger",
      "name": "Step Name",
      "summary": "Brief description of what this step does",
      "position": { "x": number, "y": number },
      "config": {
        "subject": "Email subject (for email nodes)",
        "content": "Template content with {{variables}}",
        "waitDuration": "3 days (for wait nodes)",
        "condition": "Email opened? (for condition nodes)"
      },
      "metrics": {
        "openRate": 25.5,
        "replyRate": 4.2
      }
    }
  ],
  "connections": [
    { "from": "step-id", "to": "step-id" }
  ],
  "estimatedDuration": "2-3 weeks",
  "expectedResults": {
    "estimatedReach": 1000,
    "expectedOpenRate": 25,
    "expectedReplyRate": 8,
    "estimatedMeetings": 80
  }
}

Create a logical, effective sequence with 5-8 steps including proper timing, personalization, and follow-up strategies. Include realistic performance metrics based on industry benchmarks.
`;

    try {
      const response = await this.callGeminiAPI(prompt);
      const content = response.candidates[0].content.parts[0].text;
      
      // Extract JSON from response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON found in response');
      }
      
      const sequenceData = JSON.parse(jsonMatch[0]);
      
      // Convert to ReactFlow format
      const nodes: Node[] = sequenceData.steps.map((step: any, index: number) => ({
        id: step.id || `step-${index}`,
        type: `${step.type}Node`,
        position: step.position || { x: 400, y: 100 + (index * 150) },
        data: {
          label: step.name,
          summary: step.summary,
          template: step.config.subject ? {
            subject: step.config.subject,
            content: step.config.content
          } : undefined,
          metrics: step.metrics,
          config: step.config
        }
      }));

      const edges: Edge[] = sequenceData.connections.map((conn: any, index: number) => ({
        id: `edge-${index}`,
        source: conn.from,
        target: conn.to,
        type: 'smoothstep',
        markerEnd: { type: MarkerType.ArrowClosed, color: '#6366f1' }
      }));

      return {
        name: sequenceData.name,
        description: sequenceData.description,
        nodes,
        edges,
        estimatedDuration: sequenceData.estimatedDuration,
        expectedResults: sequenceData.expectedResults
      };
    } catch (error) {
      console.error('Gemini sequence generation failed:', error);
      throw error;
    }
  }

  static async generateTemplate(request: TemplateRequest): Promise<GeneratedTemplate> {
    const prompt = `
You are an expert email marketing copywriter. Generate a high-converting ${request.type} template based on these requirements:

Type: ${request.type}
Category: ${request.category}
Description: ${request.description}
Target Audience: ${request.targetAudience}
Objective: ${request.objective}
Personalization Level: ${request.personalizationLevel}

Generate a JSON response with this exact structure:
{
  "name": "Template Name",
  "category": "${request.category}",
  "subject": "Compelling subject line (for email)",
  "content": "Template content with {{firstName}}, {{company}}, and other merge variables",
  "tags": ["tag1", "tag2", "tag3"],
  "variables": ["firstName", "company", "industry"],
  "tips": [
    "Tip 1 for using this template effectively",
    "Tip 2 for personalization",
    "Tip 3 for best results"
  ]
}

Requirements:
- Use merge variables like {{firstName}}, {{company}}, {{industry}}, {{title}} for personalization
- Keep ${request.type} content concise and action-oriented
- Include a clear call-to-action
- Make it conversational and human
- Follow ${request.personalizationLevel} personalization standards
- Optimize for ${request.objective}

${request.type === 'email' ? 'Include an engaging subject line that will increase open rates.' : ''}
${request.type === 'linkedin' ? 'Keep message under 300 characters for LinkedIn best practices.' : ''}
${request.type === 'sms' ? 'Keep message under 160 characters for SMS best practices.' : ''}
`;

    try {
      const response = await this.callGeminiAPI(prompt);
      const content = response.candidates[0].content.parts[0].text;
      
      // Extract JSON from response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON found in response');
      }
      
      const templateData = JSON.parse(jsonMatch[0]);
      
      return {
        name: templateData.name,
        category: templateData.category,
        subject: templateData.subject,
        content: templateData.content,
        tags: templateData.tags || [],
        variables: templateData.variables || [],
        tips: templateData.tips || []
      };
    } catch (error) {
      console.error('Gemini template generation failed:', error);
      throw error;
    }
  }

  static async enhanceEmailContent(
    subject: string,
    content: string,
    targetAudience: string,
    objective: string
  ): Promise<{ subject: string; content: string; improvements: string[] }> {
    const prompt = `
You are an expert email marketing optimizer. Improve this email for better performance:

Current Subject: ${subject}
Current Content: ${content}
Target Audience: ${targetAudience}
Objective: ${objective}

Provide a JSON response with:
{
  "subject": "Improved subject line",
  "content": "Enhanced email content",
  "improvements": [
    "Explanation of improvement 1",
    "Explanation of improvement 2",
    "Explanation of improvement 3"
  ]
}

Focus on:
- Increasing open rates with compelling subject
- Improving click-through rates
- Adding personalization opportunities
- Enhancing call-to-action
- Making content more engaging and conversational
`;

    try {
      const response = await this.callGeminiAPI(prompt);
      const content_response = response.candidates[0].content.parts[0].text;
      
      const jsonMatch = content_response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No valid JSON found in response');
      }
      
      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      console.error('Gemini content enhancement failed:', error);
      throw error;
    }
  }
}

export default GeminiService; 